import React, { FC, useState } from 'react';
import CampaignAppSelector from '../CampaignForms/CampaignAppsSelector/CampaignAppsSelector';
import { Application } from 'reduxState/store/application/types';
import { ICampaign } from 'reduxState/store/campaigns/types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import CreateCampaignForm from '../CampaignForms/CreateCampaignForm/CreateCampaignForm';
import { clearNewCampaignDraft } from '../CampaignForms/hooks/useCampaignForm';

/**
 * CreateGlobalCampaign component provides a form interface for creating and duplicating global campaigns
 * that can target multiple applications.
 *
 * The component consists of two main parts:
 * 1. An application selector that allows choosing which apps to target
 * 2. A campaign creation form for configuring campaign details
 *
 * The component maintains state for:
 * - Selected applications
 * - Application selection validation
 * - Form reset functionality
 *
 * It supports creating new campaigns from scratch or duplicating existing ones by
 * pre-populating the form fields with data from an existing campaign.
 *
 * @component
 * @param {Object} props - Component props
 * @param {ICampaign | null} props.campaignToDuplicate - Existing campaign to duplicate, or null for new campaign
 * @param {(campaign: ICampaign | null) => void} props.setCampaignToDuplicate - Function to update the campaign being duplicated
 * @returns {JSX.Element} A form interface containing application selection and campaign configuration
 *
 * @example
 * // Creating a new campaign
 * <CreateGlobalCampaign
 *   campaignToDuplicate={null}
 *   setCampaignToDuplicate={setCampaignToDuplicate}
 * />
 *
 * @example
 * // Duplicating an existing campaign
 * <CreateGlobalCampaign
 *   campaignToDuplicate={existingCampaign}
 *   setCampaignToDuplicate={setCampaignToDuplicate}
 * />
 */

interface ICreateGlobalCampaign {
  campaignToDuplicate: ICampaign | null;
  setCampaignToDuplicate: (campaign: ICampaign | null) => void;
  handleCloseModal: () => void;
  isModalOpen: boolean;
}

const CreateGlobalCampaign: FC<ICreateGlobalCampaign> = ({
  campaignToDuplicate,
  setCampaignToDuplicate,
  handleCloseModal,
  isModalOpen,
}) => {
  const [selectedApps, setSelectedApps] = useState<Application[]>([]);
  const [hasSelectedAppsError, setHasSelectedAppsError] = useState(false);
  const [reset, setReset] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);

  const requestClose = () => {
    if (hasFormChanges) setShowCloseConfirm(true);
    else handleCloseModal();
  };
  const confirmClose = () => {
    setShowCloseConfirm(false);
    clearNewCampaignDraft();
    handleCloseModal();
  };
  const cancelClose = () => setShowCloseConfirm(false);

  const resetSelectedApps = () => {
    setSelectedApps([]);
    setHasSelectedAppsError(false);
    setReset(true);
    // Reset the reset flag after a short delay to allow the effect to run
    setTimeout(() => setReset(false), 100);
  };

  return (
    <>
    <Dialog
      open={isModalOpen}
      classes={{ paper: 'p-3 sm:p-4 pb-4 w-[calc(100vw-16px)] max-w-[1400px] min-w-0 m-2 sm:m-4 h-auto max-h-[100dvh] overflow-y-auto overflow-x-hidden' }}
      onClose={() => requestClose()}
      fullWidth
      maxWidth="lg"
    >
      <div className="absolute top-2 right-2 sm:top-[15px] sm:right-[15px] cursor-pointer z-10 p-1" onClick={requestClose}>
        <Clear />
      </div>
      <div className="flex flex-col w-full min-w-0 max-w-full overflow-x-hidden p-4 sm:p-6 md:p-8 pb-6 bg-white min-h-0 box-border">
        <h2 className="m-0 mb-4 text-muted-dark-purple">Create Global Generic Conquesting Campaign</h2>
        <div className="mb-4">
          <h3 className="text-muted-dark-purple">Applications</h3>
          <CampaignAppSelector
            setSelectedApps={setSelectedApps}
            hasSelectedAppsError={hasSelectedAppsError}
            setHasSelectedAppsError={setHasSelectedAppsError}
            reset={reset}
            campaign={campaignToDuplicate}
          />
        </div>
        <CreateCampaignForm
          selectedApps={selectedApps}
          setHasSelectedAppsError={setHasSelectedAppsError}
          resetSelectedApps={resetSelectedApps}
          onDraftRestored={setSelectedApps}
          isCreateModalOpen={isModalOpen}
          campaignToDuplicate={campaignToDuplicate}
          setCampaignToDuplicate={setCampaignToDuplicate}
          handleCloseModal={handleCloseModal}
          onHasFormChangesChange={setHasFormChanges}
        />
      </div>
    </Dialog>
    <Dialog open={showCloseConfirm} onClose={cancelClose}>
      <DialogTitle>Discard changes?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your progress will be lost. Are you sure you want to close?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancelClose}>Cancel</Button>
        <Button onClick={confirmClose} color="error" variant="contained">
          Discard
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default CreateGlobalCampaign;
